import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ContextBar } from "../components/context-bar";
import { AdvancedJson, KeyVal, StateTag, formatAge } from "../components/phase3";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type BlueprintContent,
  type BlueprintView,
  type TemplateDetail,
  type TemplateSummary,
} from "../lib/api";

function toMessage(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong. Please try again.";
}

export function Blueprints(): JSX.Element {
  const ctx = useAppContext();

  return (
    <>
      <PageHeader title="Blueprint library">
        <p>
          Blueprints describe the <strong>DESIRED</strong> shape of a server. They are targets only — nothing here
          touches a live Discord server. Compare against the <Link to="/app/snapshot">CURRENT</Link> snapshot and preview
          differences with a <Link to="/app/dry-run">dry-run</Link>.
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to manage blueprints.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar />
          <TemplateCatalog />
          {ctx.tenantId ? (
            <BlueprintList key={ctx.tenantId} tenantId={ctx.tenantId} />
          ) : (
            <EmptyState title="Select a workspace">Choose a workspace above to see and create its blueprints.</EmptyState>
          )}
        </>
      )}
    </>
  );
}

// ── Templates ────────────────────────────────────────────────────────────────
function TemplateCatalog(): JSX.Element {
  const [templates, setTemplates] = useState<TemplateSummary[] | null>(null);
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listTemplates()
      .then((r) => setTemplates(r.templates))
      .catch((e) => setError(toMessage(e)));
  }, []);

  const openTemplate = (key: string) => {
    setDetail(null);
    api.getTemplate(key).then(setDetail).catch((e) => setError(toMessage(e)));
  };

  return (
    <section style={{ marginBottom: "1.5rem" }}>
      <h2 className="section-title">
        Templates <StateTag state="desired" />
      </h2>
      {error ? <ApiUnavailableState detail={error} /> : null}
      {templates === null ? (
        <LoadingState label="Loading templates…" />
      ) : (
        <div className="card-grid">
          {templates.map((t) => (
            <Card key={t.key} title={t.name} description={t.description}>
              <div className="row">
                <Button variant="secondary" onClick={() => openTemplate(t.key)}>
                  View template
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {detail ? <TemplateDetailView detail={detail} onClose={() => setDetail(null)} /> : null}
    </section>
  );
}

function contentCounts(content: BlueprintContent): { roles: number; categories: number; channels: number } {
  return {
    roles: content.roles?.length ?? 0,
    categories: content.categories?.length ?? 0,
    channels: content.channels?.length ?? 0,
  };
}

function TemplateDetailView({ detail, onClose }: { detail: TemplateDetail; onClose: () => void }): JSX.Element {
  const c = contentCounts(detail.content);
  return (
    <Card title={`Template — ${detail.name}`} description={detail.description}>
      <div className="kv-grid">
        <KeyVal k="Roles" v={c.roles} />
        <KeyVal k="Categories" v={c.categories} />
        <KeyVal k="Channels" v={c.channels} />
        <KeyVal k="Community type" v={detail.content.metadata?.communityType ?? "—"} />
      </div>
      <BlueprintContentPreview content={detail.content} />
      <AdvancedJson title="Advanced — read-only template JSON" value={detail.content} />
      <div className="row" style={{ marginTop: "0.75rem" }}>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </Card>
  );
}

/** A structured, read-only preview of blueprint content (not a JSON editor). */
export function BlueprintContentPreview({ content }: { content: BlueprintContent }): JSX.Element {
  return (
    <div className="bp-preview">
      <div>
        <h4 className="subhead">Roles</h4>
        <ul className="chip-list">
          {(content.roles ?? []).map((r) => (
            <li key={r.resourceKey} className="chip">
              {r.name}
              {r.protected ? <span className="marker marker--protected">protected</span> : null}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="subhead">Categories & channels</h4>
        <ul className="tree">
          {(content.categories ?? []).map((cat) => (
            <li key={cat.resourceKey}>
              <strong>{cat.name}</strong>
              <ul>
                {(content.channels ?? [])
                  .filter((ch) => ch.parentResourceKey === cat.resourceKey)
                  .map((ch) => (
                    <li key={ch.resourceKey}>
                      <span className="muted">#</span>
                      {ch.name} <span className="muted">· {ch.type}</span>
                    </li>
                  ))}
              </ul>
            </li>
          ))}
          {(content.channels ?? [])
            .filter((ch) => !ch.parentResourceKey)
            .map((ch) => (
              <li key={ch.resourceKey}>
                <span className="muted">#</span>
                {ch.name} <span className="muted">· {ch.type}</span>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
}

// ── Blueprints ───────────────────────────────────────────────────────────────
function BlueprintList({ tenantId }: { tenantId: string }): JSX.Element {
  const [blueprints, setBlueprints] = useState<BlueprintView[] | null>(null);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [cloneKey, setCloneKey] = useState("");
  const [snapshotId, setSnapshotId] = useState("");

  const load = () => {
    api
      .listBlueprints(tenantId)
      .then((r) => setBlueprints(r.blueprints))
      .catch((e) => setError(toMessage(e)));
  };

  useEffect(() => {
    load();
    api.listTemplates().then((r) => setTemplates(r.templates)).catch(() => setTemplates([]));
  }, [tenantId]); // load reads tenantId

  const createBlank = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.createBlueprint(tenantId, { name: name.trim() });
      setName("");
      load();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const cloneFromTemplate = async () => {
    if (!cloneKey) return;
    setBusy(true);
    setError(null);
    try {
      const tpl = await api.getTemplate(cloneKey);
      const bp = await api.createBlueprint(tenantId, {
        name: name.trim() || `${tpl.name} (copy)`,
        communityType: tpl.content.metadata?.communityType,
      });
      // Seed the first draft version from the template content.
      await api.createVersion(tenantId, bp.id, { content: tpl.content, notes: `Cloned from template ${tpl.key}` });
      setName("");
      setCloneKey("");
      load();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const importCurrent = async () => {
    if (!snapshotId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.importSnapshot(tenantId, { snapshotId: snapshotId.trim(), name: name.trim() || undefined });
      setSnapshotId("");
      setName("");
      load();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h2 className="section-title">Your blueprints</h2>
      {error ? <ApiUnavailableState detail={error} /> : null}

      <Card title="Create a blueprint">
        <label className="field" style={{ maxWidth: 420 }}>
          <span className="field__label">Name</span>
          <input className="field__input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Main server" aria-label="Blueprint name" />
        </label>
        <div className="create-actions">
          <div className="create-action">
            <p className="muted">Start empty and build up versions.</p>
            <Button variant="primary" disabled={busy || name.trim().length < 2} onClick={createBlank}>
              Create blank
            </Button>
          </div>
          <div className="create-action">
            <p className="muted">Clone from a template as the first draft.</p>
            <div className="row">
              <select className="field__input" value={cloneKey} onChange={(e) => setCloneKey(e.target.value)} aria-label="Template to clone">
                <option value="">Select template…</option>
                {templates.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.name}
                  </option>
                ))}
              </select>
              <Button variant="secondary" disabled={busy || !cloneKey} onClick={cloneFromTemplate}>
                Clone template
              </Button>
            </div>
          </div>
          <div className="create-action">
            <p className="muted">Import your current server from a completed snapshot.</p>
            <div className="row">
              <input className="field__input" value={snapshotId} onChange={(e) => setSnapshotId(e.target.value)} placeholder="Snapshot id" aria-label="Snapshot id to import" />
              <Button variant="secondary" disabled={busy || snapshotId.trim().length < 1} onClick={importCurrent}>
                Import current server
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {blueprints === null ? (
        <LoadingState label="Loading blueprints…" />
      ) : blueprints.length === 0 ? (
        <EmptyState title="No blueprints yet">Create one above to get started.</EmptyState>
      ) : (
        <div className="card-grid">
          {blueprints.map((b) => (
            <Card key={b.id} title={b.name} description={b.description ?? undefined}>
              <div className="kv-grid">
                <KeyVal k="Community" v={b.communityType ?? "—"} />
                <KeyVal k="Updated" v={formatAge(b.updatedAt)} />
                {b.isTemplate ? <KeyVal k="Kind" v={<StatusIndicator tone="neutral" label="template" />} /> : null}
              </div>
              <div className="row" style={{ marginTop: "0.5rem" }}>
                <Link to={`/app/blueprints/${b.id}`}>
                  <Button variant="secondary">Open</Button>
                </Link>
              </div>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
